import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BatchUploadModal = ({ isOpen, onClose, onUpload }) => {
    const [csvData, setCsvData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Upload, 2: Preview
    const [error, setError] = useState(null);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target.result;
            parseCSV(text);
        };
        reader.readAsText(file);
    };

    const parseCSV = (text) => {
        try {
            const lines = text.split('\n').filter(l => l.trim());
            if (lines.length < 2) throw new Error('CSV must have header and at least one row');

            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            const required = ['name', 'departmentcode', 'type', 'capacity'];
            const missing = required.filter(h => !headers.includes(h));

            if (missing.length > 0) {
                throw new Error(`Missing required headers: ${missing.join(', ')}`);
            }

            const parsed = lines.slice(1).map(line => {
                const values = line.split(',');
                const obj = {};
                headers.forEach((h, i) => {
                    obj[h] = values[i] ? values[i].trim() : '';
                });
                return obj;
            });

            setCsvData(parsed);
            setStep(2);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            // Transform data for service
            const formattedData = csvData.map(row => ({
                name: row.name,
                departmentCode: row.departmentcode,
                departmentName: 'From Batch', // Ideally lookup name from code if possible
                type: row.type,
                capacity: parseInt(row.capacity, 10) || 0
                // ID will be generated by service
            }));

            await onUpload(formattedData);
            onClose();
        } catch (err) {
            setError('Failed to upload batch');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-hidden flex flex-col">
                <h3 className="text-xl font-bold text-foreground mb-4">Batch Upload Rooms</h3>

                {error && (
                    <div className="bg-error/10 text-error text-sm p-3 rounded-md mb-4">{error}</div>
                )}

                <div className="flex-1 overflow-y-auto">
                    {step === 1 ? (
                        <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
                            <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
                            <p className="text-foreground font-medium mb-2">Upload CSV File</p>
                            <p className="text-sm text-muted-foreground mb-6">Headers required: Name, DepartmentCode, Type, Capacity</p>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileUpload}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                            />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-foreground">Previewing {csvData.length} rooms</p>
                            <div className="border border-border rounded-md overflow-hidden">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-muted text-muted-foreground">
                                        <tr>
                                            <th className="px-4 py-2">Name</th>
                                            <th className="px-4 py-2">Dept Code</th>
                                            <th className="px-4 py-2">Type</th>
                                            <th className="px-4 py-2">Capacity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {csvData.slice(0, 10).map((row, i) => (
                                            <tr key={i} className="border-t border-border">
                                                <td className="px-4 py-2">{row.name}</td>
                                                <td className="px-4 py-2">{row.departmentcode}</td>
                                                <td className="px-4 py-2">{row.type}</td>
                                                <td className="px-4 py-2">{row.capacity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {csvData.length > 10 && (
                                    <div className="p-2 text-center text-xs text-muted-foreground bg-muted/30">
                                        ...and {csvData.length - 10} more rows
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-border">
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    {step === 2 && (
                        <Button variant="default" onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Uploading...' : 'Confirm Upload'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BatchUploadModal;
